import { FC, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

import { getBaseUrl } from '@core/base-url';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { agreementsMutations, agreementsQueries } from '@shared/apis/agreements';
import { Button } from '@shared/components/button-new';
import { Checkbox } from '@shared/components/checkbox';
import { Loading } from '@shared/components/loading';
import { Overlay, OverlayBody, OverlayFooter, OverlayHeader, OverlayType } from '@shared/components/overlay';
import { ContainerWidths } from '@shared/constants/common';
import { useAxiosMutation, useAxiosQuery } from '@shared/hooks/axios';
import { NewButtonColour } from '@shared/types/common/button';

import { styles } from './AgreementModal.styles';
import { closeAgreement, getAgreementOpen, subscribeAgreement } from './agreementStore';

import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();

export interface AgreementModalProps extends WithStyles<typeof styles> {
  onClose: () => void;
}

const AgreementModalComponent: FC<AgreementModalProps> = ({ classes, onClose }) => {
  const [accepted, setAccepted] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);

  const agreementLatestQuery = useAxiosQuery<undefined, ArrayBuffer>(undefined, agreementsQueries.latest, {
    onSuccess: (data) => {
      if (!data || data.byteLength === 0) {
        onClose();
        return;
      }
      setPdfData(new Uint8Array(data));
    },
  });

  const agreementAcceptMutation = useAxiosMutation<undefined, undefined>(agreementsMutations.accept, {
    onSuccess: () => window.location.reload(),
  });

  const pdfFile = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData]);

  const pages = useMemo(
    () =>
      Array.from({ length: numPages }, (_, index) => (
        <Page key={index + 1} className={classes.page} pageNumber={index + 1} width={1200} />
      )),
    [numPages, classes.page]
  );

  return (
    <div className={classes.root}>
      <div className={classes.skeleton}>
        <div className={classes.skeletonSidebar}>
          <div className={classes.skeletonLogo} />
          <div className={`${classes.skeletonMenuItem} ${classes.skeletonMenuItemActive}`} />
          <div className={classes.skeletonMenuItem} />
          <div className={classes.skeletonMenuItem} />
          <div className={classes.skeletonMenuItem} />
          <div className={classes.skeletonMenuSpacer} />
          <div className={classes.skeletonMenuItem} />
          <div className={classes.skeletonMenuItem} />
          <div className={classes.skeletonMenuItem} />
        </div>
        <div className={classes.skeletonMainArea}>
          <div className={classes.skeletonNav}>
            <div className={classes.skeletonNavTab} />
            <div className={classes.skeletonNavTab} />
            <div className={classes.skeletonNavTab} />
          </div>
          <div className={classes.skeletonContent}>
            <div className={classes.skeletonBlockSmall} />
            <div className={classes.skeletonBlock} />
            <div className={classes.skeletonBlock} />
          </div>
        </div>
      </div>
      <Overlay open type={OverlayType.Modal} width={ContainerWidths.xl3} withTransition onClose={() => undefined}>
        <OverlayHeader classes={{ closeButton: classes.headerCloseButton }} heading="End User Licence Agreement" />
        <OverlayBody classes={{ root: classes.body }}>
          {agreementLatestQuery.isLoading && (
            <div className={classes.loadingContainer}>
              <Loading />
            </div>
          )}
          {agreementLatestQuery.isError && <div className={classes.errorContainer}>Failed to load agreement.</div>}
          {!agreementLatestQuery.isError && agreementAcceptMutation.isError && (
            <div className={classes.errorContainer}>Failed to accept agreement. Please try again.</div>
          )}
          {pdfFile &&
            !agreementLatestQuery.isLoading &&
            !agreementLatestQuery.isError &&
            !agreementAcceptMutation.isError && (
              <Document
                file={pdfFile}
                loading={
                  <div className={classes.loadingContainer}>
                    <Loading />
                  </div>
                }
                error={<div className={classes.errorContainer}>Failed to load PDF.</div>}
                onLoadSuccess={({ numPages: total }) => setNumPages(total)}
              >
                {pages}
              </Document>
            )}
        </OverlayBody>
        <OverlayFooter
          contentLeft={
            <Button
              colour={NewButtonColour.Ghost}
              onClick={() => {
                window.location.href = `${getBaseUrl()}logout`;
              }}
            >
              Log out
            </Button>
          }
          contentRight={
            <div className={classes.footerActions}>
              <Checkbox
                checked={accepted}
                label="I have read and agree to the End User Licence Agreement"
                onChange={setAccepted}
              />
              <Button
                colour={NewButtonColour.PrimaryNavy}
                disabled={!accepted || agreementAcceptMutation.isLoading}
                onClick={() => agreementAcceptMutation.mutate(undefined)}
              >
                {agreementAcceptMutation.isLoading ? 'Accepting...' : 'Accept'}
              </Button>
            </div>
          }
        />
      </Overlay>
    </div>
  );
};

export const AgreementModal = withStyles(styles)(AgreementModalComponent);

export const AgreementModalOutlet: FC = () => {
  const open = useSyncExternalStore(subscribeAgreement, getAgreementOpen, getAgreementOpen);

  if (!open) return null;

  return <AgreementModal onClose={closeAgreement} />;
};
